import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { V2Networks } from '@shared/utils/config/networkAddresses';
import { CovalentBalanceItem } from '@shared/types/frontend';
import { BuySellAssetControllerStages, SelectFromToListProps } from '@shared/types/frontend/assetPage';
import Link from 'next/link';
import { useLocalStorage } from 'usehooks-ts';
import { MdOutlineCheck } from 'react-icons/md';
import { toSpecialFixed } from '@/lib/misc/utils';
import getNetworkLogo from '@/lib/misc/getNetworkLogo';
import ImageWithBadge from '../../../sendFunds/ImageWithBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { useStableCoinsData } from '@/hooks/asset/useTokenData';
import useParseAssetsIntoNativeAndCash from '@/hooks/asset/useParseAssetsIntoNativeAndCash';
import { prefillsForNativeTokenDefaultSells } from '../hooks/useBuySellForm';
import { useSwap } from '@/contexts/SwapProvider';
const SelectFromToList = ({
  tokenData,
  assetData,
  setStage,
  form
}: SelectFromToListProps) => {
  const [showAllCashNatives, setShowAllCashNatives] = useState({
    show: false,
    type: 'natives'
  });
  const _tokenNetworkId = tokenData?.token?.networkId;
  const {
    data: stableCoinsData
  } = useStableCoinsData(_tokenNetworkId);
  const {
    resetAssets
  } = useSwap();
  const [, setSelectedFromToken] = useLocalStorage('selected-from-token', undefined);
  const [, setSelectedToToken] = useLocalStorage('selected-to-token', undefined);
  const {
    _allCashBalances,
    _nativeNetworkBalances,
    _otherNetworkNativeBalances
  } = useParseAssetsIntoNativeAndCash(assetData, _tokenNetworkId);
  const balancesToShow = showAllCashNatives.type === 'natives' ? _otherNetworkNativeBalances : _allCashBalances;
  const _handleOnTokenSelect = (toToken: CovalentBalanceItem) => {
    if (form.watch('tradeSide') === 'buy') {
      form.setValue('fromToken', toToken);
      setSelectedFromToken(toToken);
    } else {
      console.log('setting to token -1', toToken);
      form.setValue('toToken', toToken);
      setSelectedToToken(toToken);
    }
    setStage(BuySellAssetControllerStages.default);
    return;
  };
  const _handleShowAllCash = type => {
    setShowAllCashNatives({
      show: !showAllCashNatives.show,
      type
    });
  };
  const isTokenSelected = (tokenAddress: string, selectedAddress: string) => {
    if (!tokenAddress || !selectedAddress) {
      return false;
    }
    return tokenAddress === selectedAddress;
  };
  return <div className='h-fit flex flex-col gap-2 justify-between' data-sentry-component="SelectFromToList" data-sentry-source-file="SelectFromToList.tsx">
      <div className='flex gap-2 justify-between order-first pl-4'>
        <Button type='button' variant='blue' className='order-first rounded-full flex gap-2 items-center w-[74px] h-[31px] px-3 text-genius-cream' onClick={() => setStage(BuySellAssetControllerStages.default)} data-sentry-element="Button" data-sentry-source-file="SelectFromToList.tsx">
          <ChevronLeft className='w-[15px] h-[15px]' data-sentry-element="ChevronLeft" data-sentry-source-file="SelectFromToList.tsx" />
          <p>Back</p>
        </Button>
        <p className='basis-1/3 mr-6'>
          {form.watch('tradeSide') === 'buy' ? 'Balances' : 'Sell To'}
        </p>
        {/* Hidden div for justify-between */}
        <div />
      </div>
      {/* Balances */}
      <div className='order-1 h-full flex flex-col mb-2'>
        {form.watch('tradeSide') === 'buy' ? <>
            {_nativeNetworkBalances.length > 0 ? <>
                {_nativeNetworkBalances.map(token => {
            const isSelected = isTokenSelected(token.contract_address, form.watch('fromToken')?.contract_address);
            return <div key={`${token.contract_display_name}`}>
                      {<FormField key={`${token.contract_display_name}`} control={form.control} name={form.watch('tradeSide') === 'buy' ? 'fromToken' : 'toToken'} render={() => <FormItem>
                              <FormControl>
                                <Button type='button' variant='blue' className={`w-full flex justify-between items-center px-4 py-8 h-[80px] bg-transparent 
                                 ${isSelected && 'bg-genius-purple'}`} onClick={() => _handleOnTokenSelect(token)}>
                                  <div className='flex gap-4 items-center'>
                                    <ImageWithBadge mainImageSrc={token.logo_url} badgeImageSrc={getNetworkLogo(token.network)} size={32} badgeSize={12} />
                                    <div className='grid gap-1 justify-items-start'>
                                      <p className='flex items-center'>
                                        {token.contract_display_name}{' '}
                                        {isSelected && <MdOutlineCheck />}
                                      </p>
                                      <p className='text-xs opacity-50'>
                                        {token.native_token ? token.contract_ticker_symbol : `Cash on ${V2Networks[token.network].name}`}
                                      </p>
                                    </div>
                                    <Badge variant={token.native_token ? 'outline' : 'secondary'} className={`${token.native_token ? 'bg-genius-indigo' : 'bg-genius-blue'}`}>
                                      {isSelected ? token.native_token ? 'Default' : '' : token.native_token ? 'Fastest' : 'Fast'}
                                    </Badge>
                                  </div>

                                  <div className='grid gap-1 justify-items-end'>
                                    <p>$ {toSpecialFixed(token.usd_value).value}</p>
                                    <p className='text-xs opacity-50'>
                                      {token.formatted_balance.toFixed(2)}{' '}
                                      {token.contract_ticker_symbol}
                                    </p>
                                  </div>
                                </Button>
                              </FormControl>
                            </FormItem>} />}
                    </div>;
          })}
              </> : <p className='flex w-full justify-center mt-4'>No Balances</p>}
            {showAllCashNatives.show ? <div className='grid gap-2'>
                <div className='flex justify-between py-4'>
                  <p>All {showAllCashNatives.type === 'natives' ? 'Natives' : 'Cash'}</p>
                  <Button variant='blue' className='justify-self-center rounded-full w-[168px] h-[31px]' onClick={_handleShowAllCash}>
                    Collapse
                  </Button>
                </div>
                <div>
                  {balancesToShow?.length > 0 ? <>
                      {balancesToShow?.map(token => {
                const isSelected = isTokenSelected(token.contract_address, form.watch('fromToken')?.contract_address);
                return <div key={`${token.contract_ticker_symbol}`}>
                            {<FormField control={form.control} name={form.watch('tradeSide') === 'buy' ? 'fromToken' : 'toToken'} render={() => <FormItem>
                                    <FormControl>
                                      <Button type='button' variant={token.native_token ? 'ghost' : 'ghost'} className={`w-full flex justify-between items-center px-4 py-8 h-[80px] hover:bg-genius-purple
                                       ${isSelected && 'bg-genius-purple'}`} onClick={() => _handleOnTokenSelect(token)}>
                                        <div className='flex gap-4 items-center'>
                                          <ImageWithBadge mainImageSrc={token?.logo_urls?.token_logo_url || token?.logo_url} badgeImageSrc={getNetworkLogo(token.network)} size={32} badgeSize={12} />
                                          <div className='grid gap-1 justify-items-start'>
                                            <p className='flex items-center'>
                                              {token.contract_display_name}{' '}
                                              {isSelected && <MdOutlineCheck />}
                                            </p>
                                            <p className='text-xs opacity-50 flex items-center'>
                                              {token.native_token ? token.contract_ticker_symbol : `Cash on ${V2Networks[token.network].name}`}
                                            </p>
                                          </div>
                                          <Badge variant='secondary' className='bg-genius-blue'>
                                            Slow
                                          </Badge>
                                        </div>

                                        <div className='grid gap-1 justify-items-end'>
                                          <p>$ {toSpecialFixed(token.usd_value).value}</p>
                                          <p className='text-xs opacity-50'>
                                            {token.formatted_balance.toFixed(2)}{' '}
                                            {token.contract_ticker_symbol}
                                          </p>
                                        </div>
                                      </Button>
                                    </FormControl>
                                  </FormItem>} />}
                          </div>;
              })}
                    </> : <p>No Balances</p>}
                </div>
              </div> : <div className='flex justify-center gap-2'>
                <Button type='button' variant='blue' className='place-self-center rounded-full w-[168px] h-[31px] mt-6 mb-2' onClick={() => _handleShowAllCash('natives')}>
                  Show All Natives
                </Button>
                <Button type='button' variant='blue' className='place-self-center rounded-full w-[168px] h-[31px] mt-6 mb-2' onClick={() => _handleShowAllCash('cash')}>
                  Show All Cash
                </Button>
              </div>}
          </> : <>
            {prefillsForNativeTokenDefaultSells.filter(token => token.network === _tokenNetworkId).map(token => {
          const isSelected = isTokenSelected(token.contract_address, form.watch('toToken')?.contract_address);
          return <>
                    {token.native_token && <div key={`${token.contract_name}`}>
                        {<FormField key={`${token.contract_name}`} control={form.control} name={form.watch('tradeSide') === 'buy' ? 'fromToken' : 'toToken'} render={() => <FormItem>
                                <FormControl>
                                  <Button type='button' variant='blue' className={`w-full flex justify-between items-center px-4 py-8 h-[80px] hover:bg-genius-purple bg-transparent
                                  ${isSelected && 'bg-genius-purple'}`} onClick={() => _handleOnTokenSelect(token)}>
                                    <div className='flex gap-4 items-center'>
                                      <ImageWithBadge mainImageSrc={token.logo_url} badgeImageSrc={getNetworkLogo(token.network)} size={32} badgeSize={12} />
                                      <div className='grid gap-1 justify-items-start'>
                                        <p className='flex items-center'>
                                          {token.contract_name} {isSelected && <MdOutlineCheck />}
                                        </p>
                                        <p className='text-xs opacity-50 flex items-center'>
                                          {token.contract_ticker_symbol}
                                        </p>
                                      </div>
                                    </div>
                                    <Badge variant='outline' className='bg-genius-indigo'>
                                      Default
                                    </Badge>
                                  </Button>
                                </FormControl>
                              </FormItem>} />}
                      </div>}
                  </>;
        })}
            {stableCoinsData && stableCoinsData.length > 0 ? <>
                {stableCoinsData.map(token => {
            const isSelected = isTokenSelected(token.contract_address, form.watch('toToken')?.contract_address);
            return <div key={`${token.contract_name}`}>
                      {<FormField key={`${token.contract_name}`} control={form.control} name={form.watch('tradeSide') === 'buy' ? 'fromToken' : 'toToken'} render={() => <FormItem>
                              <FormControl>
                                <Button type='button' variant='blue' className={`w-full flex justify-between items-center px-4 py-8 h-[80px] hover:bg-genius-purple bg-transparent
                                  ${isSelected && 'bg-genius-purple'}`} onClick={() => _handleOnTokenSelect(token)}>
                                  <div className='flex gap-4 items-center'>
                                    <ImageWithBadge mainImageSrc={token.logo_url} badgeImageSrc={getNetworkLogo(token.network)} size={32} badgeSize={12} />
                                    <div className='grid gap-1 justify-items-start'>
                                      <p className='flex items-center'>
                                        {token.contract_name} {isSelected && <MdOutlineCheck />}
                                      </p>
                                      <p className='text-xs opacity-50 flex items-center'>
                                        {token.contract_ticker_symbol}
                                      </p>
                                    </div>
                                  </div>
                                  <Badge variant='secondary' className='bg-genius-blue'>
                                    {isSelected ? '' : 'Fastest'}
                                  </Badge>
                                </Button>
                              </FormControl>
                            </FormItem>} />}
                    </div>;
          })}
              </> : <p>No stable coins found</p>}
          </>}
      </div>
      {/* Show all cash */}
      <Link className={`order-last px-4 mb-4`} href={`/trade?address=${form.watch('tradeSide') === 'buy' ? form.watch('toToken')?.contract_address : form.watch('fromToken')?.contract_address}&networkId=${form.watch('tradeSide') === 'buy' ? form.watch('toToken')?.network : form.watch('fromToken')?.network}&side=${form.watch('tradeSide') === 'buy' ? 'to' : 'from'}`} data-sentry-element="Link" data-sentry-source-file="SelectFromToList.tsx">
        <Button onClick={() => {
        resetAssets();
      }} className={`w-full  h-[56px] rounded-full text-[14px]`} type='button' variant='blue' data-sentry-element="Button" data-sentry-source-file="SelectFromToList.tsx">
          {form.watch('tradeSide') === 'buy' ? 'Buy from' : 'Sell to'} Other Asset
        </Button>
      </Link>
    </div>;
};
export default SelectFromToList;